/**
 * Accounts store — localStorage-based staff account management.
 *
 * How it works:
 *  - Hardcoded accounts in permissions.ts are the "seeded" baseline.
 *  - Directors can add NEW accounts via the admin UI → stored here in localStorage.
 *  - The auth context merges both lists.
 *  - Passwords are plain-text here because there is no real backend yet.
 *    Replace with Supabase Auth before going production.
 */

import type { AppRole, OperationalRole, DemoUser } from "@/lib/auth/permissions";

export interface ManagedAccount {
  id: string;
  name: string;
  initials: string;
  email: string;
  password: string;
  role: AppRole;
  operationalRoles: OperationalRole[];
  avatarColor: string;
  description: string;
  /** true = created via the admin UI (can be edited/deleted). false = seeded. */
  managed: true;
  createdAt: string;
}

const KEY = (tenantId: string) => `opscom_managed_accounts_${tenantId}`;

function readAll(tenantId: string): ManagedAccount[] {
  if (typeof localStorage === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(KEY(tenantId)) ?? "[]") as ManagedAccount[];
  } catch {
    return [];
  }
}

function writeAll(tenantId: string, accounts: ManagedAccount[]) {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(KEY(tenantId), JSON.stringify(accounts));
}

export function getManagedAccounts(tenantId: string): ManagedAccount[] {
  return readAll(tenantId);
}

export function saveManagedAccount(
  tenantId: string,
  data: Omit<ManagedAccount, "id" | "managed" | "createdAt">
): ManagedAccount {
  const existing = readAll(tenantId);
  const account: ManagedAccount = {
    ...data,
    id: `ma-${Date.now()}`,
    managed: true,
    createdAt: new Date().toISOString(),
  };
  writeAll(tenantId, [...existing, account]);
  return account;
}

export function updateManagedAccount(
  tenantId: string,
  id: string,
  updates: Partial<Omit<ManagedAccount, "id" | "managed" | "createdAt">>
) {
  const existing = readAll(tenantId);
  writeAll(tenantId, existing.map((a) => (a.id === id ? { ...a, ...updates } : a)));
}

export function deleteManagedAccount(tenantId: string, id: string) {
  writeAll(tenantId, readAll(tenantId).filter((a) => a.id !== id));
}

/** Convert a ManagedAccount to a DemoUser shape for the auth context. */
export function toDemoUser(a: ManagedAccount): DemoUser {
  return {
    id: a.id,
    name: a.name,
    initials: a.initials,
    email: a.email,
    password: a.password,
    role: a.role,
    operationalRoles: a.operationalRoles,
    avatarColor: a.avatarColor,
    description: a.description,
  };
}
